"use strict";
/**
 * Materials-personal canonical definition — the single source of truth for the
 * `materials-personal` OpenSearch index family (owned/registered by the setup lambda;
 * backfilled + alias-swapped by the reindex script).
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants — runtime values typed against `MaterialSearchDocument`
 *       keys so the contract and the constants cannot drift, and
 *   (b) the OpenSearch template body (`buildMaterialsPersonalTemplate`) — settings
 *       (INCLUDING the `analysis` analyzers/normalizers), `dynamic: true`, and explicit
 *       `properties`.
 *
 * `materials-personal` shares `MATERIAL_FIELDS` with `materials-public` — both families
 * project `MaterialSearchDocument`. It is re-exported here for convenience so this
 * family's consumers do not need a second import; it is NOT redefined.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MATERIAL_FIELDS = void 0;
exports.buildMaterialsPersonalTemplate = buildMaterialsPersonalTemplate;
const materials_public_definition_1 = require("./materials-public-definition");
// Shared with the `materials-public` family — re-exported for convenience, never redefined.
var materials_public_definition_2 = require("./materials-public-definition");
Object.defineProperty(exports, "MATERIAL_FIELDS", { enumerable: true, get: function () { return materials_public_definition_2.MATERIAL_FIELDS; } });
/**
 * Build the composable OpenSearch index-template body for the `materials-personal` family.
 *
 * Ported from the setup lambda's inline `materials-personal` IndexDefinition (material_id,
 * identity_id, product_name, name, brand, category, material_type, color, images,
 * inventory_count, created_at, updated_at), with analyzers applied consistently with the
 * `materials-public` template: `product_name`/`name` are `text`/`fpw_text` with `keyword`
 * + `sasy` multi-fields, and the keyword facets (`brand`/`category`/`material_type`/
 * `color`) carry the `lowercase` normalizer. The full `settings.analysis` block (the
 * `fpw_text` analyzer, the `english_*` filters, and the `lowercase` normalizer) is copied
 * from `materials-public-definition.ts` so the analyzers travel with the mappings.
 * `mappings.dynamic` is the literal `true` (the BF-018 guard).
 *
 * Property keys that are members of `MATERIAL_FIELDS` are keyed by the constants so the
 * mapping types exactly the fields the constants name. The remaining contract keys not
 * present in `MATERIAL_FIELDS` (`identity_id`, `material_type`, `color`, `images`,
 * `updated_at`) and the mapping-only `inventory_count` (NOT a `MaterialSearchDocument`
 * key) are kept as plain string keys.
 */
function buildMaterialsPersonalTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [materials_public_definition_1.MATERIAL_FIELDS.material_id]: { type: 'keyword' },
                    identity_id: { type: 'keyword' },
                    [materials_public_definition_1.MATERIAL_FIELDS.product_name]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            keyword: { type: 'keyword' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [materials_public_definition_1.MATERIAL_FIELDS.name]: {
                        type: 'text',
                        analyzer: 'fpw_text',
                        fields: {
                            keyword: { type: 'keyword' },
                            sasy: { type: 'search_as_you_type' },
                        },
                    },
                    [materials_public_definition_1.MATERIAL_FIELDS.brand]: { type: 'keyword', normalizer: 'lowercase' },
                    [materials_public_definition_1.MATERIAL_FIELDS.category]: { type: 'keyword', normalizer: 'lowercase' },
                    material_type: { type: 'keyword', normalizer: 'lowercase' },
                    color: { type: 'keyword', normalizer: 'lowercase' },
                    images: { type: 'keyword' },
                    inventory_count: { type: 'integer' },
                    [materials_public_definition_1.MATERIAL_FIELDS.created_at]: { type: 'date' },
                    updated_at: { type: 'date' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'materials-personal' },
    };
}
